import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Star, ChefHat, User, MapPin, ArrowLeft,
  Mail, Lock, Building, Send, ShieldCheck, HeartHandshake
} from "lucide-react";

const IconInput = ({ icon: Icon, ...props }) => (
  <div className="relative">
    <Icon className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
    <Input className="pl-10 bg-transparent border-border focus:border-primary" {...props} />
  </div>
);

const PortalAuth = ({ onLogin, onRegister, isLoggingIn }) => {
  const navigate = useNavigate();
  const [loginEmail, setLoginEmail] = useState("");
  const [loginPassword, setLoginPassword] = useState("");
  const [registerData, setRegisterData] = useState({
    email: "", password: "", restaurant_name: "", location: "", contact_person: ""
  });

  return (
    <div className="min-h-screen bg-background" data-testid="restaurant-portal-auth">
      <header className="border-b border-border bg-card/50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center gap-4">
              <Button variant="ghost" size="sm" onClick={() => navigate('/')} className="text-muted-foreground hover:text-foreground">
                <ArrowLeft className="w-4 h-4 mr-2" />Back
              </Button>
              <div className="flex items-center gap-2">
                <ChefHat className="w-5 h-5 text-primary" />
                <span className="font-semibold">Restaurant Portal</span>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <Star className="w-5 h-5 text-primary fill-primary" />
              <span className="font-semibold gradient-text">Étoile</span>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-lg mx-auto px-4 py-12">
        <div className="text-center mb-8">
          <ChefHat className="w-12 h-12 text-primary mx-auto mb-4" />
          <h1 className="text-2xl font-bold mb-2">Restaurant Portal</h1>
          <p className="text-muted-foreground">Lernen Sie Ihre Gäste kennen, bevor sie bei Ihnen Platz nehmen</p>
        </div>

        <Card className="bg-primary/5 border-primary/20 mb-6" data-testid="portal-intro">
          <CardContent className="p-6 space-y-4">
            <p className="text-sm text-muted-foreground">
              Étoile-Gäste sind leidenschaftliche Feinschmecker, die ihre Besuche in Sternerestaurants
              dokumentieren. Gäste, die ihre Daten freigegeben haben, teilen mit Ihnen:
            </p>
            <div className="space-y-3">
              <div className="flex items-start gap-3">
                <HeartHandshake className="w-5 h-5 text-primary shrink-0 mt-0.5" />
                <p className="text-sm"><span className="font-medium">Vorlieben & Unverträglichkeiten</span> – Lieblingsgerichte, Weine, Desserts und Diät-Einschränkungen für einen perfekt vorbereiteten Service</p>
              </div>
              <div className="flex items-start gap-3">
                <Star className="w-5 h-5 text-primary shrink-0 mt-0.5" />
                <p className="text-sm"><span className="font-medium">Sterne-Historie</span> – welche Restaurants der Gast bereits besucht hat und wie erfahren er ist</p>
              </div>
              <div className="flex items-start gap-3">
                <Send className="w-5 h-5 text-primary shrink-0 mt-0.5" />
                <p className="text-sm"><span className="font-medium">Direkte Gäste-Kommunikation</span> – bedanken Sie sich nach dem Besuch oder laden Sie zu einem Aperitif aufs Haus ein</p>
              </div>
            </div>
            <p className="text-xs text-muted-foreground border-t border-border pt-3">
              <ShieldCheck className="w-3 h-3 inline mr-1" />
              Neue Konten werden vor der Freischaltung vom Étoile-Team geprüft – so bleiben die Gästedaten geschützt.
            </p>
          </CardContent>
        </Card>

        <Card className="bg-card border-border">
          <CardContent className="pt-6">
            <Tabs defaultValue="login">
              <TabsList className="grid w-full grid-cols-2 bg-muted/20">
                <TabsTrigger value="login" data-testid="portal-tab-login">Login</TabsTrigger>
                <TabsTrigger value="register" data-testid="portal-tab-register">Register</TabsTrigger>
              </TabsList>

              <TabsContent value="login">
                <form onSubmit={(e) => { e.preventDefault(); onLogin(loginEmail, loginPassword); }} className="space-y-4 mt-6">
                  <div className="space-y-2">
                    <Label className="text-sm text-muted-foreground">Email</Label>
                    <IconInput icon={Mail} type="email" placeholder="restaurant@email.com" value={loginEmail} onChange={(e) => setLoginEmail(e.target.value)} data-testid="portal-login-email" />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-sm text-muted-foreground">Password</Label>
                    <IconInput icon={Lock} type="password" placeholder="••••••••" value={loginPassword} onChange={(e) => setLoginPassword(e.target.value)} data-testid="portal-login-password" />
                  </div>
                  <Button type="submit" disabled={isLoggingIn} className="w-full h-12 bg-primary text-primary-foreground" data-testid="portal-login-submit">
                    {isLoggingIn ? "Signing in..." : "Sign In"}
                  </Button>
                </form>
              </TabsContent>

              <TabsContent value="register">
                <form onSubmit={(e) => { e.preventDefault(); onRegister(registerData); }} className="space-y-4 mt-6">
                  <div className="space-y-2">
                    <Label className="text-sm text-muted-foreground">Restaurant Name *</Label>
                    <IconInput icon={Building} type="text" placeholder="Your Restaurant" value={registerData.restaurant_name} onChange={(e) => setRegisterData({ ...registerData, restaurant_name: e.target.value })} data-testid="portal-register-restaurant-name" />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-sm text-muted-foreground">Location</Label>
                    <IconInput icon={MapPin} type="text" placeholder="City, Country" value={registerData.location} onChange={(e) => setRegisterData({ ...registerData, location: e.target.value })} />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-sm text-muted-foreground">Contact Person</Label>
                    <IconInput icon={User} type="text" placeholder="Your Name" value={registerData.contact_person} onChange={(e) => setRegisterData({ ...registerData, contact_person: e.target.value })} />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-sm text-muted-foreground">Email *</Label>
                    <IconInput icon={Mail} type="email" placeholder="restaurant@email.com" value={registerData.email} onChange={(e) => setRegisterData({ ...registerData, email: e.target.value })} data-testid="portal-register-email" />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-sm text-muted-foreground">Password *</Label>
                    <IconInput icon={Lock} type="password" placeholder="••••••••" value={registerData.password} onChange={(e) => setRegisterData({ ...registerData, password: e.target.value })} data-testid="portal-register-password" />
                  </div>
                  <Button type="submit" disabled={isLoggingIn} className="w-full h-12 bg-primary text-primary-foreground" data-testid="portal-register-submit">
                    {isLoggingIn ? "Creating account..." : "Create Account"}
                  </Button>
                </form>
              </TabsContent>
            </Tabs>
          </CardContent>
        </Card>
      </main>
    </div>
  );
};

export default PortalAuth;
